import React from "react";
import { FieldPickerField } from "../../components/field-picker/components/fields/fields.types";
import { DatasheetElement, DatasheetElementId, DatasheetElementType } from "@modules/datasheets/types/datasheet-element";
import { getUniqueFieldCombination as getUniqueFieldCombinationBase } from "@common/utils/fields";
import { useDeleteKeyPress } from "@common/hooks/delete-press";
import { DATASHEET_DEFAULT_FIELDS_DIMENSIONS, textElementDimensions } from "../../editor.constants";
import { LocalDatasheetTabDto } from "../../types/tab-dto";
import { useDatasheetSavingController } from "./saving";
import { useDatasheetElementsSelectionController } from "./selection";
import { useDatasheetFormatManagementController } from "./format-management";
import { DatasheetGridSettingsDto } from "@common/dto/datasheet/grid-settings-dto";
import { useTabsController } from "./tabs";
import { DatasheetFieldComponentGetDto } from "@common/dto/datasheet/field-component/get-dto";
import { DatasheetElementSettingsFieldComponentValue, DatasheetElementSettingsTextComponentValue } from "../../components/datasheet-element-settings/controllers/main";
import { DatasheetFieldComponentTextAlign } from "@common/dto/datasheet/field-component/text-align";
import { DatasheetFieldComponentHeadingPosition } from "@common/dto/datasheet/field-component/heading-position";
import { useDatasheetElementSettingsController } from "./datasheet-element-settings";
import { useDatasheetElementsAlignmentController } from "./alignment";
import { DatasheetTextComponentGetDto } from "@common/dto/datasheet/text-component/get-dto";
import { generateId } from "@common/utils/common";
import { DatasheetComponentTextStyle } from "@common/dto/datasheet/component-settings/text-style";
import { useDatasheetElementsCopyPasteController } from "./copy-paste";
import { DatasheetFieldComponentOriginFieldGetDto } from "@common/dto/datasheet/field-component/origin-field-dto";
import { useGridSettingsController } from "./grid-settings";
import { useDatasheetUndoRedoController } from "./undo-redo";
import { DatasheetStorage } from "@modules/datasheets/utils/datasheet-storage";
import { DatasheetLocalGetDto } from "../../types/datasheet";
import { FieldType } from "@common/dto/field/type";

export type DatasheetControllerProps = {
  selectedFieldsInFieldPicker: Array<FieldPickerField>;
  datasheet: DatasheetLocalGetDto;
  onDatasheetChange: (datasheet: DatasheetLocalGetDto) => void;
};

export const useDatasheetController = (props: DatasheetControllerProps) => {
  const { selectedFieldsInFieldPicker, datasheet, onDatasheetChange } = props;
  const datasheetElementsStorage = React.useMemo<DatasheetStorage>(() => new DatasheetStorage(), []);
  // TODO review usage of currentTabId
  const [currentTabId, setCurrentTabId] = React.useState<LocalDatasheetTabDto['id']>();
  const [currentDatasheetElementsHistoryRecordId, setCurrentDatasheetElementsHistoryRecordId] = React.useState<string>();
  const [copiedDatasheetElements, setCopiedDatasheetElements] = React.useState<Array<DatasheetElement>>([]);
  const [datasheetElements, setDatasheetElements] = React.useState<Record<DatasheetElementId, DatasheetElement>>({});
  const [selectedDatasheetElementsId, setSelectedDatasheetElementsId] = React.useState<Array<DatasheetElementId>>([]);
  const [copiedDatasheetElementsFromTab, setCopiedDatasheetElementsFromTab] = React.useState<Record<DatasheetElementId, DatasheetElement>>({});
  const [defaultFieldComponentSettings, setDefaultFieldComponentSettings] = React.useState<DatasheetElementSettingsFieldComponentValue>({
    content: {
      fontFamily: 'Arial',
      fontSize: 10,
      color: '#000000',
      showBorder: true,
      showFieldType: true,
      styles: [],
      align: DatasheetFieldComponentTextAlign.left,
      tabOrder: 1
    },
    heading: {
      displayHeading: true,
      textValue: '',
      fontFamily: 'Tahoma',
      fontSize: 8,
      color: '#000000',
      position: DatasheetFieldComponentHeadingPosition.top,
      align: DatasheetFieldComponentTextAlign.left,
      textStyles: []
    },
    helpText: {
      textValue: ''
    },
    table: undefined
  });

  const [defaultTextComponentSettings, setDefaultTextComponentSettings] = React.useState<DatasheetElementSettingsTextComponentValue>({
    fontFamily: 'Tahoma',
    fontSize: 18,
    color: '#006699',
    textStyle: [DatasheetComponentTextStyle.bold],
    text: '',
  });

  const normalizeDatasheetElementsTabOrder = React.useCallback((els: Record<DatasheetElementId, DatasheetElement>) => {
    const elements = Object.values(els);
    type Field = DatasheetElement<DatasheetFieldComponentGetDto>;
    const sortedElements = elements.sort((a, b) => (a as Field).original.taborder - (b as Field).original.taborder);
    let taborder = 0;

    const nextElements = sortedElements.reduce<Record<DatasheetElementId, DatasheetElement>>((acc, element) => {
      if (element.type === DatasheetElementType.field) {
        const updatedElement = {
          ...element,
          original: {
            ...element.original,
            taborder: taborder + 1
          }
        };

        acc[element.id] = updatedElement;

        taborder = taborder + 1;

        return acc;
      }

      acc[element.id] = element;

      return acc;
    }, {});

    return nextElements;
  }, []);

  const addNewDatasheetElementsHistoryRecord = React.useCallback((elements: Record<DatasheetElementId, DatasheetElement>, tabId?: LocalDatasheetTabDto['id']) => {
    const newHistoryRecordId = generateId();

    datasheetElementsStorage.addHistoryRecord({
      tabId: tabId ?? currentTabId!,
      currentHistoryRecordId: currentDatasheetElementsHistoryRecordId!,
      newHistoryRecordId,
      elementsForNewHistoryRecord: elements
    });

    setCurrentDatasheetElementsHistoryRecordId(newHistoryRecordId);
  }, [currentTabId, currentDatasheetElementsHistoryRecordId]);

  const updateDatasheetElementsHistoryRecord = React.useCallback((elements: Record<DatasheetElementId, DatasheetElement>, params?: { tabId?: LocalDatasheetTabDto['id'] }) => {
    datasheetElementsStorage.updateHistoryRecord({
      tabId: params?.tabId ?? currentTabId!,
      historyRecordId: currentDatasheetElementsHistoryRecordId!,
      elements
    });
  }, [currentTabId, currentDatasheetElementsHistoryRecordId]);

  const datasheetUndoRedoController = useDatasheetUndoRedoController({
    currentHistoryRecordId: currentDatasheetElementsHistoryRecordId,
    getDatasheetElementsHistorySequence: () => datasheetElementsStorage.getHistorySequence(currentTabId!),
  });

  const setTabDatasheetElements = React.useCallback((elements: Record<DatasheetElementId, DatasheetElement>, tabId?: LocalDatasheetTabDto['id']) => {
    datasheetElementsStorage.setElements(tabId ?? currentTabId!, elements);
    setDatasheetElements(elements);
  }, [currentTabId]);

  const setTabDatasheetElementsAndUpdateHistoryRecord = React.useCallback((elements: Record<DatasheetElementId, DatasheetElement>) => {
    setTabDatasheetElements(elements);
    updateDatasheetElementsHistoryRecord(elements);
  }, [setTabDatasheetElements, updateDatasheetElementsHistoryRecord]);

  const setTabDatasheetElementsAndAddHistoryRecord = React.useCallback((elements: Record<DatasheetElementId, DatasheetElement>) => {
    setTabDatasheetElements(elements);
    addNewDatasheetElementsHistoryRecord(elements);
  }, [setTabDatasheetElements, addNewDatasheetElementsHistoryRecord]);

  const getDatasheetElement = React.useCallback((id: DatasheetElementId) => {
    return datasheetElements[id];
  }, [datasheetElements]);

  const getUniqueDatasheetElementId = (field: { fieldid: number; systemField: boolean; type: FieldType; }) => getUniqueFieldCombinationBase({
    id: field.fieldid,
    system: field.systemField,
    type: field.type,
  });

  const doesFieldExistOnCurrentTab = React.useCallback((field: DatasheetFieldComponentOriginFieldGetDto) => {
    const id = getUniqueDatasheetElementId(field);

    return Boolean(datasheetElements[id]);
  }, [datasheetElements]);

  const resetSelectedDatasheetElements = React.useCallback(() => {
    setSelectedDatasheetElementsId([]);
  }, []);

  const resetSelectedAndCopiedDatasheetElements = React.useCallback(() => {
    setSelectedDatasheetElementsId([]);
    setCopiedDatasheetElements([]);
  }, []);

  const gridSettingsController = useGridSettingsController({ 
    editTabOrderActive: datasheet.gridSettings.showEditTabOrder,
    datasheet, 
    datasheetElements, 
    onDatasheetElementsChange: setTabDatasheetElementsAndUpdateHistoryRecord 
  });
  const datasheetSavingController = useDatasheetSavingController();
  const datasheetElementsSelectionController = useDatasheetElementsSelectionController({
    selectedDatasheetElementsId,
    onSelectedDatasheetElementsIdChange: setSelectedDatasheetElementsId,
    onSelectedDatasheetElementsReset: resetSelectedDatasheetElements,
    datasheetElements,
  });
  const datasheetElementsAlignmentController = useDatasheetElementsAlignmentController({
    datasheetElements,
    selectedDatasheetElementsId,
    onDatasheetElementsChange: setTabDatasheetElementsAndAddHistoryRecord,
    getDatasheetElement,
  });
  const datasheetFormatManagementController = useDatasheetFormatManagementController();
  const tabsController = useTabsController({ 
    datasheet, 
    onDatasheetChange, 
    currentTabId, 
    copiedDatasheetElementsFromTab 
  });
  const datasheetElementSettingsController = useDatasheetElementSettingsController({
    normalizeDatasheetElementsTabOrder,
    datasheetElements,
    selectedDatasheetElementsId,
    onDatasheetElementsChange: setTabDatasheetElementsAndUpdateHistoryRecord,
    getDatasheetElement,
  });
  const datasheetElementsCopyPasteController = useDatasheetElementsCopyPasteController({
    datasheetElementsOnCurrentTab: datasheetElements,
    onDatasheetElementsCopyReset: resetSelectedAndCopiedDatasheetElements,
    doesFieldExistOnCurrentTab,
    getUniqueDatasheetElementId,
    selectedDatasheetElementsId,
    getDatasheetElement,
    onDatasheetElementsChange: setTabDatasheetElementsAndAddHistoryRecord,
    copiedDatasheetElements,
    onDatasheetElementsCopy: setCopiedDatasheetElements,
  });

  React.useEffect(() => {
    if (datasheet.tabs.length) {
      const selectedTab = datasheet.tabs[0];

      if (!selectedTab) return;

      setCurrentTabId(selectedTab.id);
      datasheet.tabs.forEach((tab) => {
        datasheetElementsStorage.addTab(tab.id);
      });
      addNewDatasheetElementsHistoryRecord({}, selectedTab.id);
      // TODO add tab elements initialization to holder
    }
  }, [datasheet.id]);

  const handleSelectedTabChange = (tabId: LocalDatasheetTabDto['id']) => {
    setCurrentTabId(tabId);
    setTabDatasheetElements(datasheetElementsStorage.getElements(tabId) || {}, tabId);
    const historySequence = datasheetElementsStorage.getHistorySequence(tabId);
    const lastHistoryRecordId = historySequence?.[historySequence.length - 1];

    if (lastHistoryRecordId) {
      setCurrentDatasheetElementsHistoryRecordId(lastHistoryRecordId);
    } else {
      addNewDatasheetElementsHistoryRecord({}, tabId);
    }
  };

  const handleTabAdd = React.useCallback((tabName: string) => {
    const newTab: LocalDatasheetTabDto = {
      id: generateId(),
      name: tabName,
      texts: [],
      fields: []
    };

    onDatasheetChange({
      ...datasheet,
      tabs: [...datasheet.tabs, newTab]
    });
    datasheetElementsStorage.addTab(newTab.id);
  }, [datasheet, onDatasheetChange]);

  const handleTabDelete = () => {
    if (!currentTabId) {
      return;
    }

    const nextTabs = datasheet.tabs.filter((tab) => tab.id !== currentTabId);

    onDatasheetChange({
      ...datasheet,
      tabs: nextTabs
    });
    datasheetElementsStorage.removeTab(currentTabId);
    datasheetElementsStorage.deleteHistory(currentTabId);

    const nextTab = nextTabs[nextTabs.length - 1];

    if (nextTab) {
      handleSelectedTabChange(nextTab.id);
    }
  };

  const handleTabCopy = React.useCallback(() => {
    if (!currentTabId) {
      return;
    }

    setCopiedDatasheetElementsFromTab({
      ...datasheetElements
    });
  }, [currentTabId, datasheetElements]);

  const handleTabPaste = (tabName: string) => {
    const newTab: LocalDatasheetTabDto = {
      id: generateId(),
      name: tabName,
      texts: [], // TODO to add elements initialization upon integration
      fields: []
    };

    onDatasheetChange({
      ...datasheet,
      tabs: [...datasheet.tabs, newTab]
    });
    datasheetElementsStorage.addTab(newTab.id);
    setTabDatasheetElements(copiedDatasheetElementsFromTab, newTab.id);
    addNewDatasheetElementsHistoryRecord(copiedDatasheetElementsFromTab, newTab.id);
    setCopiedDatasheetElementsFromTab({});
  };

  const isFieldAddedToDatasheet = React.useCallback((field: FieldPickerField) => {
    const id = getUniqueDatasheetElementId({
      fieldid: field.original.id,
      systemField: field.original.system,
      type: field.original.type,
    });

    return Boolean(datasheetElements[id]);
  }, [datasheetElements]);

  const updateDatasheetElement = React.useCallback((id: DatasheetElementId, data: Partial<DatasheetElement>) => {
    const datasheetElement = getDatasheetElement(id);

    if (!datasheetElement) {
      return;
    }

    const coordinatesChanged = data.coordinates && (
      data.coordinates.x !== datasheetElement.coordinates.x || 
      data.coordinates.y !== datasheetElement.coordinates.y
    );
    
    if (coordinatesChanged && data.coordinates) {
      const coordinatesDiff = {
        x: data.coordinates.x - datasheetElement.coordinates.x,
        y: data.coordinates.y - datasheetElement.coordinates.y,
      };

      const selectedDatasheets = selectedDatasheetElementsId.reduce((acc, elementId) => {
        const element = getDatasheetElement(elementId);

        if (element) {
          const updatedElement = {
            ...element,
            coordinates: {
              x: element.coordinates.x + coordinatesDiff.x,
              y: element.coordinates.y + coordinatesDiff.y,
            }
          };

          acc[elementId] = updatedElement;
        }

        return acc;
      }, { ...datasheetElements });
      
      setTabDatasheetElements(selectedDatasheets);

      return selectedDatasheets;
    }

    const updatedElement: DatasheetElement = {
      ...datasheetElement,
      ...data,
    };
    

    const nextDatasheetElements = {
      ...datasheetElements,
      [id]: updatedElement,
    };

    setTabDatasheetElements(nextDatasheetElements);

    return nextDatasheetElements;
  }, [
    getDatasheetElement, 
    selectedDatasheetElementsId, 
    datasheetElements, 
    setTabDatasheetElements, 
    currentTabId,
    currentDatasheetElementsHistoryRecordId
  ]);

  const handleDatasheetElementChange = React.useCallback((id: DatasheetElementId, data: Partial<DatasheetElement>) => {
    const newElements = updateDatasheetElement(id, data);

    if (newElements) {
      updateDatasheetElementsHistoryRecord(newElements);
    }
  }, [updateDatasheetElement, updateDatasheetElementsHistoryRecord]);

  const updateDatasheetElementAndAddHistoryRecord = React.useCallback((id: DatasheetElementId, data: Partial<DatasheetElement>) => {
    const newElements = updateDatasheetElement(id, data);

    if (newElements) {
      addNewDatasheetElementsHistoryRecord(newElements);
    }
  }, [updateDatasheetElement, addNewDatasheetElementsHistoryRecord]);

  const handleDatasheetElementResize = updateDatasheetElementAndAddHistoryRecord;
  const handleDatasheetElementMove = updateDatasheetElementAndAddHistoryRecord;

  const handleDatasheetElementsDrop = React.useCallback(({ firstDatasheetElementCoordinates }: { firstDatasheetElementCoordinates: { x: number; y: number } }) => {
    let firstSelectedDatasheetElement: DatasheetElement | undefined;
    let previousElementId: string | undefined;
    const existingTextDatasheetElementsCount = Object.values(datasheetElements)
      .filter((x) => x.type === DatasheetElementType.field).length;

    const nextElements = {
      ...datasheetElements,
      ...selectedFieldsInFieldPicker.reduce<Record<DatasheetElementId, DatasheetElement>>((acc, field) => {
        const id = getUniqueDatasheetElementId({
          fieldid: field.original.id,
          systemField: field.original.system,
          type: field.original.type,
        });

        if (datasheetElements[id]) {
          return acc;
        }

        const previousElement = previousElementId ? acc[previousElementId] : undefined;

        const originalFieldComponent: DatasheetFieldComponentGetDto = {
          field: field.original,
          ...datasheetElementSettingsController.mapFieldComponentSettingsToElement(defaultFieldComponentSettings),
          headtext: field.original.name,
          taborder: existingTextDatasheetElementsCount + Object.keys(acc).length + 1,
        } as DatasheetFieldComponentGetDto;

        // TODO remove as

        const datasheetElement: DatasheetElement = {
          id,
          type: DatasheetElementType.field,
          original: originalFieldComponent,
          width: DATASHEET_DEFAULT_FIELDS_DIMENSIONS[field.original.type].width,
          height: DATASHEET_DEFAULT_FIELDS_DIMENSIONS[field.original.type].height,
          coordinates: {
            x: firstDatasheetElementCoordinates.x,
            y: previousElement 
              ? previousElement.coordinates.y + previousElement.height + datasheet.gridSettings.verticalGrid
              : firstDatasheetElementCoordinates.y,
          }
        };

        acc[id] = datasheetElement;
        previousElementId = id;

        if (!firstSelectedDatasheetElement) {
          firstSelectedDatasheetElement = datasheetElement;
        }

        return acc;
      }, {})
    };
    
    setSelectedDatasheetElementsId(firstSelectedDatasheetElement 
      ? [firstSelectedDatasheetElement.id]
      : []);
    setTabDatasheetElementsAndAddHistoryRecord(nextElements);
  }, [
    datasheetElements, 
    selectedFieldsInFieldPicker, 
    currentTabId, 
    datasheet.gridSettings.verticalGrid,
    defaultFieldComponentSettings,
    datasheetElementSettingsController.mapFieldComponentSettingsToElement,
    setTabDatasheetElementsAndAddHistoryRecord,
  ]);

  const handleDatasheetTextElementsDrop = React.useCallback(() => {
    const originalTextComponent: DatasheetTextComponentGetDto = datasheetElementSettingsController.mapTextComponentSettingsToElement(defaultTextComponentSettings) as DatasheetTextComponentGetDto;

    const id = generateId();
    const datasheetElement: DatasheetElement<DatasheetTextComponentGetDto>  = {
      id,
      type: DatasheetElementType.text,
      original: originalTextComponent,
      width: textElementDimensions.width,
      height: textElementDimensions.height,
      coordinates: {
        x: datasheet.gridSettings.horizontalGrid,
        y: datasheet.gridSettings.verticalGrid
      }
    };

    const nextElements = {
      ...datasheetElements,
      [id]: datasheetElement
    };
    
    setSelectedDatasheetElementsId([id]);
    setTabDatasheetElementsAndAddHistoryRecord(nextElements);
  }, [datasheetElements, setTabDatasheetElementsAndAddHistoryRecord]);

  const handleDatasheetElementsRemove = React.useCallback(() => {
    const nextDatasheetElements = { ...datasheetElements };

    selectedDatasheetElementsId.forEach((elementId) => {
      delete nextDatasheetElements[elementId];
    });

    const normalizedElements = normalizeDatasheetElementsTabOrder(nextDatasheetElements);
    setTabDatasheetElementsAndAddHistoryRecord(normalizedElements);
    datasheetElementsSelectionController.handleSelectionReset();
  }, [
    datasheetElements, 
    selectedDatasheetElementsId, 
    datasheetElementsSelectionController.handleSelectionReset, 
    currentTabId, 
    setTabDatasheetElementsAndAddHistoryRecord,
  ]);

  useDeleteKeyPress(handleDatasheetElementsRemove);

  const datasheetElementsProcessed = React.useMemo(() => {
    return Object.values(datasheetElements);
  }, [datasheetElements]);

  const handleGridSettingsChange = React.useCallback((settings: DatasheetGridSettingsDto) => {
    onDatasheetChange({
      ...datasheet,
      gridSettings: settings
    });

    if (!datasheet.gridSettings.showEditTabOrder && settings.showEditTabOrder) {
      resetSelectedAndCopiedDatasheetElements();
    }
  }, [datasheet, onDatasheetChange, resetSelectedAndCopiedDatasheetElements]);

  const handleSelectAllDatasheetElements = React.useCallback(() => {
    setSelectedDatasheetElementsId(Object.keys(datasheetElements));
  }, [datasheetElements]);

  const handleDatasheetElementsUndo = React.useCallback(() => {
    const currentHistoryRecordId = currentDatasheetElementsHistoryRecordId;
    const historySequence = datasheetElementsStorage.getHistorySequence(currentTabId!);

    if (!currentHistoryRecordId || !historySequence) {
      return;
    }

    const currentHistoryIndex = historySequence.indexOf(currentHistoryRecordId);
    const previousHistoryRecordId = historySequence[currentHistoryIndex - 1];

    if (!previousHistoryRecordId) {
      return;
    }

    const previousHistoryRecord = datasheetElementsStorage.getHistoryRecord(currentTabId!, previousHistoryRecordId);

    if (!previousHistoryRecord) {
      return;
    }

    setTabDatasheetElements({ ...previousHistoryRecord });
    setCurrentDatasheetElementsHistoryRecordId(previousHistoryRecordId);
  }, [currentDatasheetElementsHistoryRecordId, currentTabId, setTabDatasheetElements]);

  const handleDatasheetElementsRedo = React.useCallback(() => {
    const currentHistoryRecordId = currentDatasheetElementsHistoryRecordId;
    const historySequence = datasheetElementsStorage.getHistorySequence(currentTabId!);

    if (!currentHistoryRecordId || !historySequence) {
      return;
    }

    const currentHistoryIndex = historySequence.indexOf(currentHistoryRecordId);
    const nextHistoryRecordId = historySequence[currentHistoryIndex + 1];

    if (!nextHistoryRecordId) {
      return;
    }

    const nextHistoryRecord = datasheetElementsStorage.getHistoryRecord(currentTabId!, nextHistoryRecordId);

    if (!nextHistoryRecord) {
      return;
    }

    setTabDatasheetElements({ ...nextHistoryRecord });
    setCurrentDatasheetElementsHistoryRecordId(nextHistoryRecordId);
  }, [currentDatasheetElementsHistoryRecordId, currentTabId, setTabDatasheetElements]);

  return {
    ...datasheetSavingController,
    ...datasheetElementsSelectionController,
    ...datasheetFormatManagementController,
    ...tabsController,
    ...datasheetElementSettingsController,
    ...datasheetElementsAlignmentController,
    ...datasheetElementsCopyPasteController,
    ...gridSettingsController,
    ...datasheetUndoRedoController,
    handleTabAdd,
    handleTabDelete,
    handleTabCopy,
    handleTabPaste,
    handleDatasheetElementsUndo,
    handleDatasheetElementsRedo,
    handleDatasheetElementResize,
    handleDatasheetElementMove,
    handleSelectAllDatasheetElements,
    defaultFieldComponentSettings,
    setDefaultFieldComponentSettings,
    handleGridSettingsChange,
    handleDatasheetElementsDrop,
    handleDatasheetTextElementsDrop,
    currentTabId,
    handleSelectedTabChange,
    selectedFieldsInFieldPicker,
    isFieldAddedToDatasheet,
    datasheetElementsProcessed,
    handleDatasheetElementChange,
    defaultTextComponentSettings,
    setDefaultTextComponentSettings
  };
};